import type { ReactNode } from "react";
import { generatePath, Link } from "react-router-dom";
import type { CrmRecordDetail, CrmRecordTypeFieldName } from "@/lib/api/crm/masters";
import { cn } from "@/lib/utils";
import { RoutePath } from "../../routerPaths";
import { fieldGroupsFor } from "../recordFields";
import { recordTypeAccentClasses, recordTypeIcons } from "../recordTypes";
import { AddressList } from "./AddressList";
import { ContactList } from "./ContactList";
import { MobileLoginList } from "./MobileLoginList";
import { RecordFieldGroups, RecordNotes } from "./RecordFields";
import { RecordTypeBadge } from "./RecordTypeBadge";

export function recordElementId(recordId: string): string {
  return `crm-record-${recordId}`;
}

function SideHeading({ title, count }: { title: string; count: number }) {
  return (
    <h4 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
      {title}
      {count > 0 && (
        <span className="rounded-full bg-gray-200 px-1.5 py-px text-[10px] font-semibold text-gray-600">{count}</span>
      )}
    </h4>
  );
}

export function RecordCard({
  record,
  fields,
  selected,
  actions,
  linkName = true,
}: {
  record: CrmRecordDetail;
  /** This record type's fields, from GET /crm/rules. */
  fields: CrmRecordTypeFieldName[];
  selected: boolean;
  /** Extra controls shown at the top right of the card. */
  actions?: ReactNode;
  /** The name links to the record's details page; off on the details page itself. */
  linkName?: boolean;
}) {
  const Icon = recordTypeIcons[record.record_type];
  const notes = fields.includes("notes") ? record.notes : null;
  const fieldGroups = fieldGroupsFor(fields, "detail");

  return (
    <article
      id={recordElementId(record.id)}
      className={cn(
        "scroll-mt-6 overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow",
        selected ? "border-blue-300 shadow-md ring-2 ring-blue-500/60" : "hover:shadow-md",
      )}
      data-testid={recordElementId(record.id)}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-sm",
              recordTypeAccentClasses[record.record_type],
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold leading-tight text-gray-900">
              {linkName ? (
                <Link
                  to={generatePath(RoutePath.CrmRecordDetails, { recordId: record.id })}
                  className="hover:text-blue-700 hover:underline"
                  data-testid={`crm-record-name-link-${record.id}`}
                >
                  {record.long_name}
                </Link>
              ) : (
                record.long_name
              )}
            </h3>
            <div className="mt-1.5">
              <RecordTypeBadge type={record.record_type} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {selected && (
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
              Viewing
            </span>
          )}
        </div>
      </header>

      <div className="grid border-t lg:grid-cols-5">
        <div className="space-y-6 p-5 lg:col-span-3">
          {notes && <RecordNotes notes={notes} />}
          <RecordFieldGroups record={record} groups={fieldGroups} />
          {!notes && fieldGroups.length === 0 && (
            <p className="text-sm text-gray-400">No other details for this record.</p>
          )}
        </div>
        <div className="space-y-6 border-t bg-gray-50/70 p-5 lg:col-span-2 lg:border-l lg:border-t-0">
          <section>
            <SideHeading title="Contacts" count={record.contacts.length} />
            <ContactList contacts={record.contacts} />
          </section>
          <section>
            <SideHeading title="Mobile Login" count={record.auth_users.length} />
            <MobileLoginList authUsers={record.auth_users} />
          </section>
          <section>
            <SideHeading title="Addresses" count={record.addresses.length} />
            <AddressList addresses={record.addresses} />
          </section>
        </div>
      </div>
    </article>
  );
}
